import React from 'react';
import { IPollMyUnit, IPollQuestion } from '../../frontend-libs/evlapp-types';

import { useAppSelector, useAppThunkDispatch } from '../../common/hooks';
import { getPoll, selectPoll, setQuestionChoice } from '../../common/store/pollSlice';
import { setFlashMessage } from '../../common/store/uiSlice';

import { Button, CardQuestion, Modal, Text, TipMessage } from '../../common/components';
import { t } from '../../common/i18n';

import { sendPollQuestionVote } from '../services/PollService';

import { useModal } from '../../common/components/Modal/useModal';
import QuestionSendModal from '../QuestionSendModal';
import { MyVotingUnits } from './MyVotingUnits';

export const TabDetail: React.FC = () => {
  const namespace = 'Poll.Detail.TabDetail';

  const dispatch = useAppThunkDispatch();
  const poll = useAppSelector(selectPoll);
  const [isShownModal, setIsShownModal] = useModal();
  const answeredAll = (poll.questions ?? []).find((question: IPollQuestion) => !question.answer) ? false : true;

  const myUnits: IPollMyUnit[] = poll.myUnits ?? [];
  const votedUnits = myUnits.filter((unit) => unit.voted);
  // canVote = false: přihlášený už nemá co odeslat — typicky za jeho jednotku platně
  // hlasoval spoluvlastník nebo zástupce (nebo hlasování neběží). Jen čtení, žádné akce.
  const canVote = poll.canVote === true;
  const showAlreadyVoted = !canVote && votedUnits.length > 0;
  const showPartiallyVoted = canVote && votedUnits.length > 0;

  const sendPollQuestion = async (questionId: string, answer: 'yes' | 'no' | 'undecided') => {
    try {
      await sendPollQuestionVote(answer, questionId, poll.id!);
      dispatch(setQuestionChoice({ questionId, answer }));
    } catch (error) {
      // 422 = mezitím za jednotku platně hlasoval někdo jiný — přenačíst stav a vysvětlit
      const rejected = (error as { status?: number })?.status === 422;
      dispatch(setFlashMessage({ message: t(rejected ? 'voteRejected' : 'voteFailed', namespace), type: 'error' }));
      if (rejected && poll.id) dispatch(getPoll(poll.id));
    }
  };

  return (
    <>
      <MyVotingUnits units={myUnits} />
      {showAlreadyVoted && (
        <TipMessage className="mb-3" title={t('AlreadyVoted.title', namespace)}>
          {t('AlreadyVoted.text', namespace)}
        </TipMessage>
      )}
      {showPartiallyVoted && (
        <TipMessage className="mb-3" title={t('PartiallyVoted.title', namespace)}>
          {t('PartiallyVoted.text', namespace)}
        </TipMessage>
      )}
      <Text size="h2" color="Neutral800" className="mb-3">
        {t('title', namespace)}
      </Text>
      {poll?.questions?.map((question: IPollQuestion, index: number) => {
        return (
          <CardQuestion
            key={question.id}
            title={question.name}
            label={`${t('Question.label', namespace)} ${index + 1}`}
            detailLink={`question/${question.id}`}
            answer={question.answer}
            onYes={() => sendPollQuestion(question.id, 'yes')}
            onNo={() => sendPollQuestion(question.id, 'no')}
            onUndecided={() => sendPollQuestion(question.id, 'undecided')}
            isEditable={canVote}
          />
        );
      })}
      {canVote && (
        <>
          <br />
          <Button
            variant="primary"
            title={t('Poll.Question.Send.buttonSend')}
            onClick={setIsShownModal}
            size="default"
          />
        </>
      )}
      <Modal isShown={isShownModal} onHide={setIsShownModal}>
        <QuestionSendModal stepNumber={answeredAll ? 2 : 1} pollId={poll.id!} />
      </Modal>
    </>
  );
};
